import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Form, FormInstance, Input, Modal, notification, Popconfirm, Row, Select, Space, Table, Tabs, Tag, Upload, Grid } from "antd";
import { DeleteOutlined, SearchOutlined, UploadOutlined } from "@ant-design/icons";
import { FaRegFileAlt, FaUniversity } from "react-icons/fa";
import locale from 'antd/es/locale/pt_BR';
import Highlighter from "react-highlight-words";
import moment from "moment";

import { IAutorizacao, IBancoComErro, IContaComErro, IContaSaldoDeContas, IOrgaoAutorizacaoItem, IOrgaoComErro, ISolicitacaoAtivacao } from "../../../interfaces";
import { IBancoVanBancaria } from "../../../../interfaces";
import { ICreateCredenciais, ICreateCredenciaisVan, ICreateIdAplicacao, IDeleteAutorizacao, IDescobertaCnpjContaResponse, IGetAutorizacoesBancoDoBrasil, IGetBancosVanBancaria, IGetContaById, IGetContasComErro, IGetContasInativas, IGetDescobertaCnpjContaComErro, IGetDocumentoVanSignedUrl, IUpdateCredenciais, IUpdateCredenciaisVan, IUpdateIdAplicacao, IUpdateNumeroRequerimentoVan } from "./interfaces";
import { IUpdateConta } from "../AlinhamentoDeContasEFontes/interfaces";
import { IOrgao } from "../../../../Orgaos/interfaces";

import { EmitirErro } from "../../../../../../services/EmitirErro";
import SolicitacoesDeAtivacaoService from "../../../../../../services/http/services/SaldoDeContas/SolicitacoesDeAtivacaoService";
import OrgaoService from "../../../../../../services/http/services/SaldoDeContas/OrgaoService";
import { mask } from "../../../../../../utils/MascaraDeCampos";
import InputObservacaoEtapa from "../../components/InputObservacaoEtapa";
import { removeAcento } from "../../../../../../utils/RemoveAcentos";
import { useGetColumnSearchPropsObj } from "../../../../../../hooks/FilterTable/objString";
import { useGetColumnSearchPropsData } from "../../../../../../hooks/FilterTable/date";
import { onlyNumbers } from "../../../../utils";
import EtapaInfo from "../../components/EtapaInfo";
import TabTitleCount from "../../components/TabTitleCount";
import ContasComErroContent from "./components/ContasComErroContent";
import CardInformacao from "../../../../components/CardInformacao";
import { CorrigirCnpjsBB } from "./components/CorrigirCnpjsBB";
import { BancosVanBancaria } from "./components/BancosVanBancaria";
import BotaoLimparFiltros, { checkHasActiveFilters } from "../../../../components/BotaoLimparFiltros";
import BotaoLimparSelecao from "../../../../components/BotaoLimparSelecao";

const readTxtFile = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
            resolve(reader.result as string);
        };

        reader.onerror = () => {
            reject(reader.error);
        };

        reader.readAsText(file, 'UTF-8');
    });
};

const parseKeyValueTxt = (content: string): Record<string, string> => {
    const result: Record<string, string> = {};

    content
        .split(/\r?\n/)
        .forEach((line) => {
            const trimmed = line.trim();

            // ignora linhas vazias ou comentários
            if (!trimmed || trimmed.startsWith('#')) return;

            const [key, ...rest] = trimmed.split('=');

            if (!key || rest.length === 0) return;

            result[key.trim()] = rest.join('=').trim();
        });

    return result;
};

interface IProps {
    solicitacao: ISolicitacaoAtivacao | null;
    onUpdateOrgao: (orgao: IOrgao) => void;
    onEtapaIniciada: (solicitacao: ISolicitacaoAtivacao) => void;
    onEtapaConcluida: (solicitacao: ISolicitacaoAtivacao) => void;
    onUpdateObservacao: (solicitacao: ISolicitacaoAtivacao) => void;
    onGetAutorizacoesBancoDoBrasil: (data: IGetAutorizacoesBancoDoBrasil) => Promise<IOrgaoAutorizacaoItem[]>;
    onGetContasComErro: (data: IGetContasComErro) => Promise<IOrgaoComErro[]>;
    onGetContasInativas: (data: IGetContasInativas) => Promise<IOrgaoComErro[]>;
    onCreateIdAplicacao: (data: ICreateIdAplicacao) => Promise<IAutorizacao | null>;
    onUpdateIdAplicacao: (data: IUpdateIdAplicacao) => Promise<IAutorizacao | null>;
    onCreateCredenciais: (data: ICreateCredenciais) => Promise<IAutorizacao | null>;
    onUpdateCredenciais: (data: IUpdateCredenciais) => Promise<IAutorizacao | null>;
    onDeleteAutorizacao: (data: IDeleteAutorizacao) => Promise<void>;
    onDescobreCnpjContaComErro?: (data: IGetDescobertaCnpjContaComErro) => Promise<IDescobertaCnpjContaResponse>;
    onGetContaById?: (data: IGetContaById) => Promise<IContaSaldoDeContas>;
    onUpdateConta?: (data: IUpdateConta) => Promise<IContaSaldoDeContas | null>;
    onContaCorrigida?: () => void;
    isManutencao?: boolean;
    onGetBancosVanBancaria?: (data: IGetBancosVanBancaria) => Promise<IBancoVanBancaria[]>;
    onUpdateNumeroRequerimentoVan?: (data: IUpdateNumeroRequerimentoVan) => Promise<IBancoVanBancaria | null>;
    onCreateCredenciaisVan?: (data: ICreateCredenciaisVan) => Promise<IBancoVanBancaria | null>;
    onUpdateCredenciaisVan?: (data: IUpdateCredenciaisVan) => Promise<IBancoVanBancaria | null>;
    onGetDocumentoVanSignedUrl?: (data: IGetDocumentoVanSignedUrl) => Promise<{ url?: string; link?: string } | any>;
}

const { useBreakpoint } = Grid

export const IntegracaoComBancos = ({
    solicitacao,
    onUpdateOrgao,
    onEtapaIniciada,
    onEtapaConcluida,
    onUpdateObservacao,
    onGetAutorizacoesBancoDoBrasil,
    onGetContasComErro,
    onGetContasInativas,
    onCreateIdAplicacao,
    onUpdateIdAplicacao,
    onCreateCredenciais,
    onUpdateCredenciais,
    onDeleteAutorizacao,
    onDescobreCnpjContaComErro,
    onGetContaById,
    onUpdateConta,
    onContaCorrigida,
    isManutencao = false,
    onGetBancosVanBancaria,
    onUpdateNumeroRequerimentoVan,
    onCreateCredenciaisVan,
    onUpdateCredenciaisVan,
    onGetDocumentoVanSignedUrl,
}: IProps) => {

    const [orgaosBancoDoBrasil, setOrgaosBancoDoBrasil] = useState<IOrgaoAutorizacaoItem[] | null>(null);
    const [orgaosComErro, setOrgaosComErro] = useState<IOrgaoComErro[] | null>(null);
    const [orgaosComContasInativas, setOrgaosComContasInativas] = useState<IOrgaoComErro[] | null>(null);
    const [bancosVan, setBancosVan] = useState<IBancoVanBancaria[] | null>(null);
    const [dadosDescoberta, setDadosDescoberta] = useState<IDescobertaCnpjContaResponse | null>(null);
    const [isLoadingOrgaos, setIsLoadingOrgaos] = useState<boolean>(true);
    const [isLoadingDescoberta, setIsLoadingDescoberta] = useState<boolean>(false);
    const [hasOneRowSelected, setHasOneRowSelected] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
    const [selectedRowKeys, setSelectedRowKeys] = useState<Array<number>>([]);
    const [selectedOrgao, setSelectedOrgao] = useState<IOrgaoAutorizacaoItem | null>(null);
    const [isIdAplicacaoModalVisible, setIsIdAplicacaoModalVisible] = useState<boolean>(false);
    const [isCredenciaisModalVisible, setIsCredenciaisModalVisible] = useState<boolean>(false);
    const [isSubmitingIdAplicacao, setIsSubmitingIdAplicacao] = useState<boolean>(false);
    const [isSubmitingCredenciais, setIsSubmitingCredenciais] = useState<boolean>(false);

    const screens = useBreakpoint();

    const formIdAplicacaoRef = useRef<FormInstance<any>>(null);
    const formCredenciaisRef = useRef<FormInstance<any>>(null);

    const etapa = solicitacao?.etapasSolicitacaoAtivacao?.find((e) => e.etapa.ordem === 3);

    const filteredOrgaosComErro = useMemo(() => {
        return orgaosComErro?.filter((e) => e.bancos.length !== 0);
    }, [orgaosComErro]);

    const filteredOrgaosComContasInativas = useMemo(() => {
        return orgaosComContasInativas?.filter((e) => e.bancos.length !== 0);
    }, [orgaosComContasInativas]);

    const countContasNaoIntegradas = useMemo(() => {
        const contasLengthByOrgaos = filteredOrgaosComErro?.reduce((prevValue, currentValue) => {
            const contasLengthByBancos = currentValue.bancos.reduce((prevValueBancos, currentValueBancos) => {
                return prevValueBancos + currentValueBancos.contas.length;
            }, 0);

            return prevValue + contasLengthByBancos;
        }, 0);

        return contasLengthByOrgaos;
    }, [filteredOrgaosComErro]);

    const countContasInativas = useMemo(() => {
        const contasLengthByOrgaos = filteredOrgaosComContasInativas?.reduce((prevValue, currentValue) => {
            const contasLengthByBancos = currentValue.bancos.reduce((prevValueBancos, currentValueBancos) => {
                return prevValueBancos + currentValueBancos.contas.length;
            }, 0);

            return prevValue + contasLengthByBancos;
        }, 0);

        return contasLengthByOrgaos;
    }, [filteredOrgaosComContasInativas]);

    const onSelectChange = useCallback((selectedRowKeys): void => {
        if (selectedRowKeys.length > 1) {
            setSelectedRowKeys([selectedRowKeys[1]]);
            setHasOneRowSelected(true);
        } else if (selectedRowKeys.length === 0) {
            setSelectedRowKeys(selectedRowKeys);
            setHasOneRowSelected(false);
        } else {
            setSelectedRowKeys([selectedRowKeys[0]]);
            setHasOneRowSelected(true);
        }
    }, [selectedRowKeys]);

    const enableButtonGerenciarCredenciais = useMemo(() => {
        if (selectedRowKeys.length !== 1) {
            return false;
        }

        const orgao = orgaosBancoDoBrasil?.find((e) => e.id === selectedRowKeys[0]);

        if (!orgao) {
            return false;
        }

        return !!orgao.autorizacaoBB && !!orgao.autorizacaoBB.bbItemId;
    }, [selectedRowKeys, orgaosBancoDoBrasil]);

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
        columnWidth: '1%',
        type: 'radio' as any
    };

    const handleDeleteAutorizacao = useCallback(async () => {
        if (selectedRowKeys.length == 1) {
            const orgaoSelecionado = orgaosBancoDoBrasil?.find((e) => e.id === selectedRowKeys[0]);

            if (solicitacao && orgaoSelecionado?.autorizacaoBB) {
                try {
                    await onDeleteAutorizacao({
                        autorizacaoId: orgaoSelecionado.autorizacaoBB.id,
                        solicitacaoId: solicitacao.id
                    });

                    setOrgaosBancoDoBrasil((prevState) => {
                        if (!prevState) {
                            return null;
                        }

                        const newData = [...prevState];

                        const orgaoIndex = newData.findIndex((e) => e.id === orgaoSelecionado.autorizacaoBB?.orgaoVinculadoId);

                        if (orgaoIndex >= 0) {
                            newData[orgaoIndex].autorizacaoBB = null;
                            newData[orgaoIndex].concluidaEm = null;
                        }

                        return newData;
                    });

                    notification.success({
                        message: 'Sucesso',
                        description: 'Autorização deletada com sucesso!',
                    });
                } catch (error) {
                    EmitirErro(error, true, 'deleteAutorizacao_2304onasdasjnod023kmopkas');
                }
            }
        }
    }, [selectedRowKeys, solicitacao]);

    const handleClickToggleContaAtiva = useCallback(async ({ contaId, ativa }: { contaId: number, ativa: boolean }) => {
        try {
            if (!solicitacao) {
                return;
            }

            const updatedConta = await SolicitacoesDeAtivacaoService.updateContaAtiva({
                contaId,
                ativa,
                solicitacaoId: solicitacao.id,
            });

            setOrgaosComErro((prevStateComErro) => {
                if (!prevStateComErro) {
                    return null;
                }

                const newStateComErro = structuredClone(prevStateComErro);

                const orgaoIndex = newStateComErro.findIndex((e: IOrgaoComErro) => e.id === updatedConta.orgaoVinculadoId);

                if (orgaoIndex < 0) {
                    return prevStateComErro;
                }

                const bancoIndex = newStateComErro[orgaoIndex].bancos.findIndex((e: IBancoComErro) => e.id === updatedConta.bancoId);

                if (bancoIndex < 0) {
                    return prevStateComErro;
                }

                const contaIndex = newStateComErro[orgaoIndex].bancos[bancoIndex].contas.findIndex((e: IContaComErro) => e.id === updatedConta.id);

                if (contaIndex < 0) {
                    return prevStateComErro;
                }

                const orgaoOriginal = structuredClone(newStateComErro[orgaoIndex]);

                const bancoOriginal = structuredClone(newStateComErro[orgaoIndex].bancos[bancoIndex]);

                const contaInativada = newStateComErro[orgaoIndex].bancos[bancoIndex].contas.splice(contaIndex, 1)[0];

                contaInativada.ativa = false;
                contaInativada.com_erro_integracao = false;

                setOrgaosComContasInativas((prevStateInativas) => {
                    if (!prevStateInativas) {
                        return null;
                    }

                    const newStateInativas = structuredClone(prevStateInativas);

                    const orgaoExists = newStateInativas.findIndex((e: IOrgaoComErro) => e.id === contaInativada.orgaoVinculadoId);

                    if (orgaoExists < 0) {
                        return [
                            ...newStateInativas,
                            {
                                ...orgaoOriginal,
                                bancos: [
                                    {
                                        ...bancoOriginal,
                                        contas: [contaInativada],
                                    },
                                ],
                            },
                        ];
                    }

                    const bancoExists = newStateInativas[orgaoExists].bancos.findIndex((e: IBancoComErro) => e.id === contaInativada.bancoId);

                    if (bancoExists < 0) {
                        newStateInativas[orgaoExists].bancos.push({
                            ...bancoOriginal,
                            contas: [contaInativada],
                        });

                        return newStateInativas;
                    }

                    const contaJaExiste = newStateInativas[orgaoExists].bancos[bancoExists].contas.some((c: IContaComErro) => c.id === contaInativada.id);

                    if (!contaJaExiste) {
                        newStateInativas[orgaoExists].bancos[bancoExists].contas.push(contaInativada);
                    }

                    return newStateInativas;
                });

                if (newStateComErro[orgaoIndex].bancos[bancoIndex].contas.length === 0) {
                    newStateComErro[orgaoIndex].bancos.splice(bancoIndex, 1);
                }

                return newStateComErro;
            });

            notification.success({
                message: 'Sucesso',
                description: `Conta ${updatedConta.ativa ? "ativada" : "inativada"} com sucesso!`,
            });
        } catch (error) {
            EmitirErro(error, true, 'updateContaAtiva_2304onasdasjnod023kmopkas');
        }
    }, [solicitacao]);

    const handleSaveIdAplicacao = useCallback(() => {
        try {
            formIdAplicacaoRef.current?.validateFields()
                .then(async (values: any) => {
                    if (!solicitacao) {
                        return;
                    }

                    try {
                        setIsSubmitingIdAplicacao(true);

                        if (selectedOrgao && selectedOrgao.autorizacaoBB) {
                            const updateIdAplicacaoResponse = await onUpdateIdAplicacao({
                                autorizacaoBbId: selectedOrgao.autorizacaoBB.id,
                                bbAplicacaoId: values.idAplicacao,
                                versaoApi: values.versaoApi,
                                solicitacaoId: solicitacao.id,
                            });

                            if (!updateIdAplicacaoResponse) {
                                return;
                            }

                            notification.success({
                                message: 'Sucesso',
                                description: 'ID aplicação atualizado com sucesso!',
                            });

                            setIsSubmitingIdAplicacao(false);
                            setOrgaosBancoDoBrasil((prevState) => {
                                if (!prevState) {
                                    return null;
                                }

                                const newData = [...prevState];

                                const orgaoIndex = newData.findIndex((e) => e.id === updateIdAplicacaoResponse.orgaoVinculadoId);

                                if (orgaoIndex >= 0) {
                                    newData[orgaoIndex].autorizacaoBB = updateIdAplicacaoResponse;
                                    newData[orgaoIndex].concluidaEm = updateIdAplicacaoResponse.concluidaEm;
                                }

                                return newData;
                            });

                            handleCloseModals();

                            return;
                        }

                        const createIdAplicacaoResponse = await onCreateIdAplicacao({
                            orgaoVinculadoId: values.orgaoVinculadoId,
                            bbAplicacaoId: values.idAplicacao,
                            versaoApi: values.versaoApi,
                            solicitacaoId: solicitacao.id,
                        });

                        if (!createIdAplicacaoResponse) {
                            return;
                        }

                        notification.success({
                            message: 'Sucesso',
                            description: 'ID aplicação criado com sucesso!',
                        });

                        setIsSubmitingIdAplicacao(false);
                        setOrgaosBancoDoBrasil((prevState) => {
                            if (!prevState) {
                                return null;
                            }

                            const newData = [...prevState];

                            const orgaoIndex = newData.findIndex((e) => e.id === createIdAplicacaoResponse.orgaoVinculadoId);

                            if (orgaoIndex >= 0) {
                                newData[orgaoIndex].autorizacaoBB = createIdAplicacaoResponse;
                                newData[orgaoIndex].concluidaEm = createIdAplicacaoResponse.concluidaEm;
                            }

                            return newData;
                        });

                        handleCloseModals();
                    } catch (error) {
                        setIsSubmitingIdAplicacao(false);
                        EmitirErro(error, true, 'idAplicacaoSaldoDeContas_0213asldo2o3w0jasdmo0j2o');
                    }
                })
                .catch(() => {
                    notification.error({
                        message: 'Erro',
                        description:
                            'Preencha os campos obrigatorios!',
                    });
                });

        } catch (error) {
            EmitirErro(error, true, 'idAplicacaoSaldoDeContas_0213asldo2o3w0jasdmo0j2o');
        }
    }, [selectedOrgao, solicitacao]);

    const handleSaveCredenciais = useCallback(() => {
        try {
            formCredenciaisRef.current?.validateFields()
                .then(async (values: any) => {
                    if (!solicitacao) {
                        return;
                    }

                    try {
                        setIsSubmitingCredenciais(true);

                        if (selectedOrgao && selectedOrgao.autorizacaoBB && selectedOrgao.autorizacaoBB.credencialBB) {
                            const updateCredenciaisResponse = await onUpdateCredenciais({
                                appKey: values.appKey,
                                basic: values.basic,
                                clientId: values.clientId,
                                clientSecret: values.clientSecret,
                                autorizacaoBbId: selectedOrgao?.autorizacaoBB?.id!,
                                solicitacaoId: solicitacao.id,
                            });

                            if (!updateCredenciaisResponse) {
                                return;
                            }

                            notification.success({
                                message: 'Sucesso',
                                description: 'Credenciais atualizadas com sucesso!',
                            });

                            setIsSubmitingCredenciais(false);
                            setOrgaosBancoDoBrasil((prevState) => {
                                if (!prevState) {
                                    return null;
                                }

                                const newData = [...prevState];

                                const orgaoIndex = newData.findIndex((e) => e.id === updateCredenciaisResponse.orgaoVinculadoId);

                                if (orgaoIndex >= 0) {
                                    newData[orgaoIndex].autorizacaoBB = updateCredenciaisResponse;
                                    newData[orgaoIndex].concluidaEm = updateCredenciaisResponse.concluidaEm;
                                }

                                return newData;
                            });

                            handleCloseModals();

                            return;
                        }

                        const createCredenciaisResponse = await onCreateCredenciais({
                            appKey: values.appKey,
                            basic: values.basic,
                            clientId: values.clientId,
                            clientSecret: values.clientSecret,
                            autorizacaoBbId: selectedOrgao?.autorizacaoBB?.id!,
                            solicitacaoId: solicitacao.id,
                        });

                        if (!createCredenciaisResponse) {
                            return;
                        }

                        notification.success({
                            message: 'Sucesso',
                            description: 'Credenciais cadastradas com sucesso!',
                        });

                        setIsSubmitingCredenciais(false);
                        setOrgaosBancoDoBrasil((prevState) => {
                            if (!prevState) {
                                return null;
                            }

                            const newData = [...prevState];

                            const orgaoIndex = newData.findIndex((e) => e.id === createCredenciaisResponse.orgaoVinculadoId);

                            if (orgaoIndex >= 0) {
                                newData[orgaoIndex].autorizacaoBB = createCredenciaisResponse;
                                newData[orgaoIndex].concluidaEm = createCredenciaisResponse.concluidaEm;
                            }

                            return newData;
                        });

                        handleCloseModals();
                    } catch (error) {
                        setIsSubmitingCredenciais(false);
                        EmitirErro(error, true, 'credenciaisSaldoDeContas_0213asldo2o3w0jasdmo0j2o');
                    }
                })
                .catch(() => {
                    notification.error({
                        message: 'Erro',
                        description:
                            'Preencha os campos obrigatorios!',
                    });
                });

        } catch (error) {
            EmitirErro(error, true, 'credenciaisSaldoDeContas_0213asldo2o3w0jasdmo0j2o');
        }
    }, [selectedOrgao, solicitacao]);

    const clickBotaoEditarUsuario = useCallback((): void => {
        if (selectedRowKeys.length == 1) {
            const orgaoSelecionado = orgaosBancoDoBrasil?.find((e) => e.id === selectedRowKeys[0]);

            if (orgaoSelecionado) {
                setSelectedOrgao(orgaoSelecionado);

                formIdAplicacaoRef.current?.setFieldsValue({
                    orgaoVinculadoId: orgaoSelecionado.id,
                    idAplicacao: orgaoSelecionado.autorizacaoBB?.bbItemId || '',
                    versaoApi: orgaoSelecionado.autorizacaoBB?.bbVersaoApi || 2,
                });

                setIsIdAplicacaoModalVisible(true);
            }
        }
    }, [selectedRowKeys, orgaosBancoDoBrasil]);

    const handleClickGerenciarCredenciais = useCallback((): void => {
        if (selectedRowKeys.length == 1) {
            const orgaoSelecionado = orgaosBancoDoBrasil?.find((e) => e.id === selectedRowKeys[0]);

            if (orgaoSelecionado && orgaoSelecionado.autorizacaoBB) {
                setSelectedOrgao(orgaoSelecionado);

                formCredenciaisRef.current?.setFieldsValue({
                    appKey: orgaoSelecionado.autorizacaoBB?.credencialBB?.appKey,
                    basic: orgaoSelecionado.autorizacaoBB?.credencialBB?.basic,
                    clientId: orgaoSelecionado.autorizacaoBB?.credencialBB?.clientId,
                    clientSecret: orgaoSelecionado.autorizacaoBB?.credencialBB?.clientSecret,
                });

                setIsCredenciaisModalVisible(true);
            }
        }
    }, [selectedRowKeys, orgaosBancoDoBrasil]);

    const handleGetBancosVan = useCallback(async (): Promise<IBancoVanBancaria[]> => {
        if (onGetBancosVanBancaria) {
            return await onGetBancosVanBancaria({
                solicitacaoId: solicitacao?.id,
                orgaoId: solicitacao?.orgao?.id,
            });
        }
        if (isManutencao) {
            const orgaoId = solicitacao?.orgao?.id;
            if (!orgaoId) return [];
            return await OrgaoService.getBancosVanBancaria({
                orgaoId,
            });
        }
        if (!solicitacao?.id) return [];
        return await SolicitacoesDeAtivacaoService.getBancosVanBancaria({
            solicitacaoId: solicitacao.id,
        });
    }, [solicitacao, onGetBancosVanBancaria, isManutencao]);

    const handleUpdateNumeroRequerimentoVan = useCallback(async (data: IUpdateNumeroRequerimentoVan): Promise<IBancoVanBancaria | null> => {
        if (onUpdateNumeroRequerimentoVan) {
            return await onUpdateNumeroRequerimentoVan(data);
        }
        if (isManutencao) {
            const orgaoId = data.orgaoId || solicitacao?.orgao?.id;
            if (!orgaoId) return null;
            return await OrgaoService.updateNumeroRequerimento({
                orgaoId,
                autorizacaoId: data.autorizacaoId,
                numeroRequerimento: data.numeroRequerimento,
            });
        }
        const solicitacaoId = data.solicitacaoId || solicitacao?.id;
        if (!solicitacaoId) return null;
        return await SolicitacoesDeAtivacaoService.updateNumeroRequerimento({
            solicitacaoId,
            autorizacaoId: data.autorizacaoId,
            numeroRequerimento: data.numeroRequerimento,
        });
    }, [onUpdateNumeroRequerimentoVan, isManutencao, solicitacao]);

    const handleCreateCredenciaisVan = useCallback(async (data: ICreateCredenciaisVan): Promise<IBancoVanBancaria | null> => {
        if (onCreateCredenciaisVan) {
            return await onCreateCredenciaisVan(data);
        }
        if (isManutencao) {
            const orgaoId = data.orgaoId || solicitacao?.orgao?.id;
            if (!orgaoId) return null;
            return await OrgaoService.createCredenciaisVanBancaria({
                orgaoId,
                autorizacaoId: data.autorizacaoId,
                clientId: data.clientId,
                clientSecret: data.clientSecret,
            });
        }
        const solicitacaoId = data.solicitacaoId || solicitacao?.id;
        if (!solicitacaoId) return null;
        return await SolicitacoesDeAtivacaoService.createCredenciaisVanBancaria({
            solicitacaoId,
            autorizacaoId: data.autorizacaoId,
            clientId: data.clientId,
            clientSecret: data.clientSecret,
        });
    }, [onCreateCredenciaisVan, isManutencao, solicitacao]);

    const handleUpdateCredenciaisVan = useCallback(async (data: IUpdateCredenciaisVan): Promise<IBancoVanBancaria | null> => {
        if (onUpdateCredenciaisVan) {
            return await onUpdateCredenciaisVan(data);
        }
        if (isManutencao) {
            const orgaoId = data.orgaoId || solicitacao?.orgao?.id;
            if (!orgaoId) return null;
            return await OrgaoService.updateCredenciaisVanBancaria({
                orgaoId,
                autorizacaoId: data.autorizacaoId,
                clientId: data.clientId,
                clientSecret: data.clientSecret,
            });
        }
        const solicitacaoId = data.solicitacaoId || solicitacao?.id;
        if (!solicitacaoId) return null;
        return await SolicitacoesDeAtivacaoService.updateCredenciaisVanBancaria({
            solicitacaoId,
            autorizacaoId: data.autorizacaoId,
            clientId: data.clientId,
            clientSecret: data.clientSecret,
        });
    }, [onUpdateCredenciaisVan, isManutencao, solicitacao]);

    const handleGetDocumentoSignedUrlVan = useCallback(async (data: IGetDocumentoVanSignedUrl): Promise<{ url?: string; link?: string } | any> => {
        if (onGetDocumentoVanSignedUrl) {
            return await onGetDocumentoVanSignedUrl(data);
        }
        if (isManutencao) {
            const orgaoId = data.orgaoId || solicitacao?.orgao?.id;
            if (!orgaoId) return null;
            return await OrgaoService.getDocumentoVanBancariaSignedUrl({
                orgaoId,
                autorizacaoId: data.autorizacaoId,
                documentoId: data.documentoId,
            });
        }
        const solicitacaoId = data.solicitacaoId || solicitacao?.id;
        if (!solicitacaoId) return null;
        return await SolicitacoesDeAtivacaoService.getDocumentoVanBancariaSignedUrl({
            solicitacaoId,
            autorizacaoId: data.autorizacaoId,
            documentoId: data.documentoId,
        });
    }, [onGetDocumentoVanSignedUrl, isManutencao, solicitacao]);

    const fetchDescobertaData = useCallback(async () => {
        if (isManutencao || !onDescobreCnpjContaComErro || !solicitacao) {
            return;
        }

        try {
            setIsLoadingDescoberta(true);
            const data = await onDescobreCnpjContaComErro({ solicitacaoId: solicitacao.id });
            setDadosDescoberta(data);
        } catch (error) {
            EmitirErro(error, true, 'onDescobreCnpjContaComErro_error');
        } finally {
            setIsLoadingDescoberta(false);
        }
    }, [isManutencao, onDescobreCnpjContaComErro, solicitacao]);

    const handleTabChange = useCallback((activeKey: string) => {
        if (activeKey === 'corrigir-cnpjs-bb') {
            fetchDescobertaData();
        }
    }, [fetchDescobertaData]);

    const findData = async () => {
        try {
            if (!solicitacao) {
                return;
            }

            setIsLoadingOrgaos(true);

            const [
                autorizacoesData,
                contasComErroData,
                contasInativasData,
                bancosVanData,
            ] = await Promise.all([
                onGetAutorizacoesBancoDoBrasil({ solicitacaoId: solicitacao.id }),
                onGetContasComErro({ solicitacaoId: solicitacao.id }),
                onGetContasInativas({ solicitacaoId: solicitacao.id }),
                handleGetBancosVan(),
            ]);

            setOrgaosBancoDoBrasil(autorizacoesData);
            setOrgaosComErro(contasComErroData);
            setOrgaosComContasInativas(contasInativasData);
            if (bancosVanData) {
                setBancosVan(bancosVanData);
            }
            setIsLoadingOrgaos(false);
        } catch (error) {
            setIsLoadingOrgaos(false);
            EmitirErro(error, true, 'findAutorizacoesBancoDoBrasil_203oasdjasdno08jo12');
        }
    }

    const handleResetRowSelection = useCallback(() => {
        setSelectedRowKeys([]);
        setHasOneRowSelected(false);
    }, []);

    const handleSearch = useCallback((selectedKeys, confirm, dataIndex): void => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    }, []);

    const handleReset = useCallback((clearFilters): void => {
        clearFilters();
        setSearchText('');
    }, []);

    const onclickTable = useCallback((record: IOrgaoAutorizacaoItem, row: any): void => {

        if (selectedRowKeys.includes(record.id)) {
            let filter = selectedRowKeys.filter((val) => {
                return val != record.id
            })
            if (filter.length === 1) {
                setSelectedRowKeys(filter);
                setHasOneRowSelected(true);
            } else if (filter.length === 0) {
                setSelectedRowKeys(filter);
                setHasOneRowSelected(false);
            } else {
                setSelectedRowKeys(filter);
            }

        } else {
            if (selectedRowKeys.length == 0) {
                setSelectedRowKeys([record.id]);
                setHasOneRowSelected(true);
            } else {
                setSelectedRowKeys([record.id]);
                setHasOneRowSelected(true);
            }
        }
    }, [selectedRowKeys]);

    const getColumnSearchProps = useCallback((
        dataIndex,
        valueFormatterBeforeCompare?: (value: string) => string,
    ) => {
        let searchInput: any = '';
        return ({
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
                <div style={{ padding: 8 }}>
                    <Input
                        ref={node => {
                            searchInput = node;
                        }}
                        placeholder={`Digite...`}
                        value={selectedKeys[0]}
                        onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        style={{ width: 188, marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Pesquisar
                        </Button>
                        <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                            Limpar
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: any) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
            onFilter: (value: any, record: any) => {

                const recordValue = record[dataIndex]?.toString();

                let trecord = removeAcento(valueFormatterBeforeCompare ? valueFormatterBeforeCompare(recordValue) : recordValue);

                const valueToCompare = removeAcento(valueFormatterBeforeCompare ? valueFormatterBeforeCompare(value) : value);

                return record[dataIndex] ? trecord.includes(valueToCompare) : false
            },
            onFilterDropdownVisibleChange: (visible: any) => {
                if (visible) {
                    setTimeout(() => searchInput.select());
                }
            },
            render: (text: any) =>
                searchedColumn === dataIndex ? (
                    <Highlighter
                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                        searchWords={[searchText]}
                        autoEscape
                        textToHighlight={text ? text.toString() : ''}
                    />
                ) : (
                    text
                ),
        })
    }, [searchText, searchedColumn, handleSearch, handleReset]);

    const columnSearchPropsAutorizacaoBB = useGetColumnSearchPropsObj('autorizacaoBB', 'bbItemId');
    const columnSearchPropsDataConcluidaEm = useGetColumnSearchPropsData('concluidaEm');

    const handleClearAllFilters = useCallback(() => {
        setFilteredInfo({});
        setSearchText('');
        setSearchedColumn('');
    }, []);

    const hasActiveFilters = useMemo(() => {
        return checkHasActiveFilters(filteredInfo, searchText);
    }, [filteredInfo, searchText]);

    const handleCloseModals = useCallback(() => {
        setSelectedOrgao(null);
        setIsIdAplicacaoModalVisible(false);
        setIsCredenciaisModalVisible(false);
        formIdAplicacaoRef.current?.setFieldsValue({
            orgaoVinculadoId: '',
            idAplicacao: '',
            versaoApi: null,
        });
        formCredenciaisRef.current?.setFieldsValue({
            appKey: '',
            basic: '',
            clientId: '',
            clientSecret: '',
        });
    }, []);

    const columns = [
        {
            title: 'Órgão',
            dataIndex: 'nome',
            key: 'nome',
            filteredValue: filteredInfo.nome || null,
            ...getColumnSearchProps('nome'),
        },
        {
            title: 'CNPJ',
            dataIndex: 'cnpj',
            key: 'cnpj',
            filteredValue: filteredInfo.cnpj || null,
            ...getColumnSearchProps('cnpj', onlyNumbers),
            width: '14%',
            render: (value: string) => {
                const maskedValue = mask(value, [
                    "99.999.999/9999-99"
                ]);

                return maskedValue;
            }
        },
        {
            title: 'ID aplicação',
            dataIndex: 'autorizacaoBB',
            key: 'autorizacaoBB',
            filteredValue: filteredInfo.autorizacaoBB || null,
            ...columnSearchPropsAutorizacaoBB,
            width: '9%',
            render: (autorizacaoBB: IAutorizacao | null) => {
                return autorizacaoBB?.bbItemId || '';
            }
        },
        {
            title: 'Status',
            dataIndex: 'id',
            key: 'id',
            filteredValue: filteredInfo.id || null,
            width: '12%',
            filters: [
                {
                    text: 'Não cadastrado',
                    value: 'Não cadastrado',
                },
                {
                    text: 'Aguardando aprovação',
                    value: 'Aguardando aprovação',
                },
                {
                    text: 'Aprovado',
                    value: 'Aprovado',
                },
            ],
            onFilter: (value: any, record: IOrgaoAutorizacaoItem) => {
                const status = !record.autorizacaoBB ? "Não cadastrado" : !record.autorizacaoBB.credencialBB ? 'Aguardando aprovação' : 'Aprovado';

                return value === status;
            },
            render: (val: number, record: IOrgaoAutorizacaoItem) => {
                let cor = '';

                const status = !record.autorizacaoBB ? "Não cadastrado" : !record.autorizacaoBB.credencialBB ? 'Aguardando aprovação' : 'Aprovado';

                switch (status) {
                    case "Não cadastrado":
                        cor = '#D4D429';
                        break;
                    case "Aguardando aprovação":
                        cor = '#2B98FF';
                        break;
                    case "Aprovado":
                        cor = '#7CBC3B';
                        break;
                    default:
                        cor = ''
                        break;
                }
                return (
                    <span>
                        {
                            status ? (
                                <Tag color={cor}>
                                    <span style={{ fontWeight: 600 }} >
                                        {status}
                                    </span>
                                </Tag>
                            ) : null
                        }
                    </span>
                )
            },
        },
        {
            title: 'Concluida em',
            dataIndex: 'concluidaEm',
            key: 'concluidaEm',
            filteredValue: filteredInfo.concluidaEm || null,
            ...columnSearchPropsDataConcluidaEm,
            width: '11%',
            sorter: (a: any, b: any) => {

                let aMonMen: any = null
                let bMonMen: any = null
                if (a.concluidaEm) {
                    aMonMen = moment(a.concluidaEm).unix()
                } else {
                    aMonMen = 0
                }
                if (b.concluidaEm) {
                    bMonMen = moment(b.concluidaEm).unix()
                } else {
                    bMonMen = 0
                }

                return aMonMen - bMonMen
            },
            showSorterTooltip: false,
            render: (concluidaEm: string | null) => (
                concluidaEm ? moment(concluidaEm).format('DD/MM/YYYY HH:mm') : null
            ),
        },
    ];

    useEffect(() => {
        findData();
    }, []);

    const items = useMemo(() => {
        if (!solicitacao) {
            return;
        }

        return [
            {
                key: 'cnpjs-a-liberar-banco-do-brasil',
                label: "CNPJ's a liberar no Banco do Brasil",
                children: (
                    <>
                        <Col span={24}>
                            <div
                                style={{
                                    position: 'sticky',
                                    top: 84,
                                    zIndex: 2,
                                    width: 'fit-content'
                                }}
                            >
                                <Row>
                                    <Col>
                                        <Button type="primary"
                                            style={{ marginRight: 5, marginBottom: 5 }}
                                            className="btn_warning_cewuyfgfvewofc"
                                            onClick={clickBotaoEditarUsuario}
                                            disabled={!hasOneRowSelected}
                                        >
                                            Gerenciar ID aplicação
                                        </Button>
                                    </Col>

                                    <Col>
                                        <Button type="primary"
                                            style={{ marginRight: 5, marginBottom: 5 }}
                                            className="btn_warning_cewuyfgfvewofc"
                                            onClick={handleClickGerenciarCredenciais}
                                            disabled={!enableButtonGerenciarCredenciais}
                                        >
                                            Gerenciar credenciais
                                        </Button>
                                    </Col>

                                    <Col>
                                        <Popconfirm
                                            title="Deseja excluir a autorização?"
                                            onConfirm={handleDeleteAutorizacao}
                                            onCancel={() => { }}
                                            okText="Sim"
                                            cancelText="Não"
                                            disabled={!enableButtonGerenciarCredenciais}
                                        >
                                            <Button
                                                type="primary"
                                                danger
                                                disabled={!enableButtonGerenciarCredenciais}
                                                style={{ marginRight: 5, marginBottom: 5 }}
                                            >
                                                <DeleteOutlined />
                                            </Button>
                                        </Popconfirm>
                                    </Col>

                                    <Col>
                                        <BotaoLimparFiltros
                                            hasActiveFilters={hasActiveFilters}
                                            onClick={handleClearAllFilters}
                                        />
                                    </Col>

                                    <Col>
                                        <BotaoLimparSelecao
                                            hasSelectedRows={selectedRowKeys.length > 0}
                                            onClick={handleResetRowSelection}
                                        />
                                    </Col>
                                </Row>
                            </div>

                            <div className='headerAbaixodosBtnTableList_ihabdfvohewvbfopu' />

                            <Table
                                rowKey={(record) => record.id}
                                style={{ marginTop: -34 }}
                                size="small"
                                onRow={(record, rowIndex) => {
                                    return {
                                        onClick: event => { onclickTable(record, rowIndex) },
                                    };
                                }}
                                rowSelection={rowSelection}
                                columns={columns}
                                dataSource={orgaosBancoDoBrasil || []}
                                pagination={{ locale: { items_per_page: '' } }}

                                locale={locale.Table}
                                loading={isLoadingOrgaos}
                                onChange={(pagination, filters, sorter, extra) => {
                                    setFilteredInfo(filters || {});
                                    handleResetRowSelection();
                                }}
                            />
                        </Col>
                    </>
                )
            },
            {
                key: 'bancos-van-bancaria',
                label: "Bancos VAN",
                children: (
                    <BancosVanBancaria
                        solicitacao={solicitacao}
                        isManutencao={isManutencao}
                        bancosVan={bancosVan}
                        isLoading={isLoadingOrgaos}
                        onGetBancosVanBancaria={handleGetBancosVan}
                        onUpdateNumeroRequerimento={handleUpdateNumeroRequerimentoVan}
                        onCreateCredenciais={handleCreateCredenciaisVan}
                        onUpdateCredenciais={handleUpdateCredenciaisVan}
                        onDeleteAutorizacao={onDeleteAutorizacao}
                        onGetDocumentoSignedUrl={handleGetDocumentoSignedUrlVan}
                        onBancosVanChange={setBancosVan}
                    />
                )
            },
            {
                key: 'contas-nao-integradas',
                label: (
                    <TabTitleCount
                        descricao="Contas não integradas"
                        count={countContasNaoIntegradas}
                        badgeBackgroundColor="#EB5757"
                        badgeTextColor="#FFFFFF"
                    />
                ),
                children: (
                    <ContasComErroContent
                        data={filteredOrgaosComErro}
                        isLoadingOrgaos={isLoadingOrgaos}
                        onClickToggleContaAtiva={handleClickToggleContaAtiva}
                        emptyText="Não há contas não integradas"
                        isManutencao={isManutencao}
                    />
                )
            },
            {
                key: 'contas-inativas',
                label: (
                    <TabTitleCount
                        descricao="Contas inativas"
                        count={countContasInativas}
                        badgeBackgroundColor="#EB5757"
                        badgeTextColor="#FFFFFF"
                    />
                ),
                children: (
                    <ContasComErroContent
                        data={filteredOrgaosComContasInativas}
                        isLoadingOrgaos={isLoadingOrgaos}
                        onClickToggleContaAtiva={handleClickToggleContaAtiva}
                        emptyText="Não há contas inativas"
                    />
                )
            },
            (!isManutencao && onDescobreCnpjContaComErro && onGetContaById && onUpdateConta) ? (
                {
                    key: 'corrigir-cnpjs-bb',
                    label: "Corrigir CNPJ's do BB",
                    children: (
                        <CorrigirCnpjsBB
                            solicitacaoId={solicitacao.id}
                            dadosDescoberta={dadosDescoberta}
                            setDadosDescoberta={setDadosDescoberta}
                            isLoading={isLoadingDescoberta}
                            onGetContaById={onGetContaById}
                            onUpdateConta={onUpdateConta}
                            onContaCorrigida={onContaCorrigida}
                        />
                    )
                }
            ) : undefined,
            !isManutencao ? (
                {
                    key: 'demais-configuracoes-etapa',
                    label: 'Demais configurações da etapa',
                    children: (
                        <>
                            <InputObservacaoEtapa
                                solicitacaoId={solicitacao.id}
                                etapa={etapa}
                                onUpdateObservacao={onUpdateObservacao}
                                style={{
                                    padding: 0,
                                }}
                            />
                        </>
                    )
                }
            ) : undefined
        ].filter((e) => e !== undefined) as any[];
    }, [
        solicitacao,
        onEtapaConcluida,
        onUpdateObservacao,
        onUpdateOrgao,
        etapa,
        orgaosBancoDoBrasil,
        clickBotaoEditarUsuario,
        hasOneRowSelected,
        handleClickGerenciarCredenciais,
        enableButtonGerenciarCredenciais,
        handleDeleteAutorizacao,
        rowSelection,
        columns,
        isLoadingOrgaos,
        handleResetRowSelection,
        handleClearAllFilters,
        hasActiveFilters,
        filteredOrgaosComErro,
        handleClickToggleContaAtiva,
        filteredOrgaosComContasInativas,
        countContasInativas,
        countContasNaoIntegradas,
        dadosDescoberta,
        isLoadingDescoberta,
        isManutencao,
        onDescobreCnpjContaComErro,
        onGetContaById,
        onUpdateConta,
        onContaCorrigida,
        bancosVan,
        handleGetBancosVan,
        handleUpdateNumeroRequerimentoVan,
        handleCreateCredenciaisVan,
        handleUpdateCredenciaisVan,
        handleGetDocumentoSignedUrlVan,
        onDeleteAutorizacao,
    ]);

    if (!solicitacao) {
        return null;
    }

    return (
        <>
            <Row>
                {
                    !isManutencao && (
                        <EtapaInfo
                            iniciadaEm={etapa?.iniciadaEm}
                            concluidaEm={etapa?.concluidaEm}
                            descricao={etapa?.etapa.descricao}
                            solicitacaoId={solicitacao.id}
                            etapa={etapa}
                            onEtapaIniciada={onEtapaIniciada}
                            onEtapaConcluida={onEtapaConcluida}
                        />
                    )
                }

                <div
                    style={{
                        width: '100%',
                        ...(!isManutencao ? {
                            opacity: etapa?.iniciadaEm ? 1 : 0.5,
                            pointerEvents: etapa?.iniciadaEm ? 'all' : 'none'
                        } : {}),
                    }}
                >
                    <Tabs
                        type="line"
                        items={items}
                        onChange={handleTabChange}
                        style={{
                            margin: '0 10px',
                        }}
                    />
                </div>
            </Row >


            <Modal
                title={selectedOrgao ? "Editar ID aplicação" : "Cadastrar ID aplicação"}
                open={isIdAplicacaoModalVisible}
                onCancel={handleCloseModals}
                footer={null}
                width={800}
            >
                <Row
                    gutter={[8, 8]}>
                    <Col
                        xs={24}
                        sm={12}
                    >
                        <CardInformacao
                            IconComponent={FaUniversity}
                            title="NOME"
                            value={selectedOrgao?.nome || "--"}
                            iconSize={13}
                            fontSize={12}
                            style={{
                                padding: '4px 6px'
                            }}
                        />
                    </Col>

                    <Col
                        xs={24}
                        sm={12}
                    >
                        <CardInformacao
                            IconComponent={FaRegFileAlt}
                            title="CNPJ"
                            value={selectedOrgao?.cnpj ? mask(selectedOrgao?.cnpj, ["99.999.999/9999-99"]) : ''}
                            iconSize={13}
                            fontSize={12}
                            style={{
                                padding: '4px 6px'
                            }}
                        />
                    </Col>
                </Row>

                <Form
                    ref={formIdAplicacaoRef}
                    style={{ marginTop: 10 }}
                    initialValues={{
                        orgaoVinculadoId: selectedOrgao?.id,
                        idAplicacao: selectedOrgao?.autorizacaoBB?.bbItemId || '',
                        versaoApi: selectedOrgao?.autorizacaoBB?.bbVersaoApi || 2,
                    }}
                    layout="vertical"
                >
                    <Form.Item name="orgaoVinculadoId" hidden>
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name="idAplicacao"
                        label="ID aplicação"
                        rules={[
                            {
                                required: true,
                                message: "Campo obrigatório!",
                            },
                        ]}
                    >
                        <Input placeholder="Informe o ID da aplicação" />
                    </Form.Item>

                    <Form.Item
                        name="versaoApi"
                        label="Versão da API"
                        rules={[
                            {
                                required: true,
                                message: "Campo obrigatório!",
                            },
                        ]}
                    >
                        <Select
                            options={[
                                {
                                    label: 1,
                                    value: 1,
                                },
                                {
                                    label: 2,
                                    value: 2,
                                }
                            ]}
                            placeholder="Informe a versão da API"
                        />
                    </Form.Item>

                    <Row>
                        <Col span={24} style={{ textAlign: "end" }}>
                            <Button
                                style={{ marginRight: 10 }}
                                onClick={handleCloseModals}
                            >
                                Cancelar
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                onClick={handleSaveIdAplicacao}
                                loading={isSubmitingIdAplicacao}
                            >
                                Salvar
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </Modal>

            <Modal
                title={selectedOrgao ? "Editar credenciais da aplicação" : "Cadastrar credenciais da aplicação"}
                open={isCredenciaisModalVisible}
                onCancel={handleCloseModals}
                footer={null}
                width={800}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: !screens.md ? 'wrap' : 'nowrap',
                        rowGap: 8
                    }}
                >
                    <div
                        style={{
                            flex: 1
                        }}
                    >
                        <Row
                            gutter={[8, 8]}>
                            <Col
                                xs={24}
                                sm={12}
                                md={8}
                            >
                                <CardInformacao
                                    IconComponent={FaUniversity}
                                    title="NOME"
                                    value={selectedOrgao?.nome || "--"}
                                    iconSize={13}
                                    fontSize={12}
                                    style={{
                                        padding: '4px 6px'
                                    }}
                                />
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                md={8}
                            >
                                <CardInformacao
                                    IconComponent={FaRegFileAlt}
                                    title="CNPJ"
                                    value={selectedOrgao?.cnpj ? mask(selectedOrgao.cnpj, ["99.999.999/9999-99"]) : ''}
                                    iconSize={13}
                                    fontSize={12}
                                    style={{
                                        padding: '4px 6px'
                                    }}
                                />
                            </Col>

                            <Col
                                xs={24}
                                sm={12}
                                md={8}
                            >
                                <CardInformacao
                                    IconComponent={FaRegFileAlt}
                                    title="ID APLICAÇÃO"
                                    value={selectedOrgao?.autorizacaoBB?.bbItemId || "--"}
                                    iconSize={13}
                                    fontSize={12}
                                    style={{
                                        padding: '4px 6px'
                                    }}
                                />
                            </Col>
                        </Row>
                    </div>

                    <Upload
                        accept=".txt"
                        multiple={false}
                        showUploadList={false}
                        disabled={isSubmitingCredenciais}
                        beforeUpload={async (e) => {
                            const content = await readTxtFile(e);
                            const values = parseKeyValueTxt(content);

                            formCredenciaisRef.current?.setFieldsValue({
                                appKey: values.appKey,
                                basic: values.basic,
                                clientId: values.clientID,
                                clientSecret: values.clientSecret,
                            });

                            notification.info({
                                message: 'Campos preenchidos',
                                description: 'Os campos foram preenchidos com base no arquivo TXT. Revise as informações e clique em Salvar para concluir.',
                            });

                            return false;
                        }}
                    >
                        <Button
                            icon={<UploadOutlined />}
                            className="botaoViewProcessoJuridico"
                            type="primary"
                            size="small"
                            style={{
                                borderRadius: 4,
                                minWidth: 'fit-content',
                                padding: '0 8px'
                            }}
                        >
                            Importar TXT
                        </Button>
                    </Upload>
                </div>

                <Form
                    ref={formCredenciaisRef}
                    style={{ marginTop: 10 }}
                    initialValues={{
                        appKey: selectedOrgao?.autorizacaoBB?.credencialBB?.appKey,
                        basic: selectedOrgao?.autorizacaoBB?.credencialBB?.basic,
                        clientId: selectedOrgao?.autorizacaoBB?.credencialBB?.clientId,
                        clientSecret: selectedOrgao?.autorizacaoBB?.credencialBB?.clientSecret,
                    }}
                    layout="vertical"
                >
                    <Form.Item
                        name="basic"
                        label="Basic"
                        rules={[
                            {
                                required: true,
                                message: "Campo obrigatório!",
                            },
                        ]}
                    >
                        <Input.TextArea placeholder="Informe o Basic" style={{ minHeight: 150 }} />
                    </Form.Item>

                    <Form.Item
                        name="appKey"
                        label="App Key"
                        rules={[
                            {
                                required: true,
                                message: "Campo obrigatório!",
                            },
                        ]}
                    >
                        <Input placeholder="Informe o App Key" />
                    </Form.Item>

                    <Form.Item
                        name="clientId"
                        label="Client ID"
                        rules={[
                            {
                                required: true,
                                message: "Campo obrigatório!",
                            },
                        ]}
                    >
                        <Input.TextArea placeholder="Informe o Client ID" />
                    </Form.Item>

                    <Form.Item
                        name="clientSecret"
                        label="Client Secret"
                        rules={[
                            {
                                required: true,
                                message: "Campo obrigatório!",
                            },
                        ]}
                    >
                        <Input.TextArea placeholder="Informe o Client Secret" style={{ minHeight: 80 }} />
                    </Form.Item>

                    <Row>
                        <Col span={24} style={{ textAlign: "end" }}>
                            <Button
                                style={{ marginRight: 10 }}
                                onClick={handleCloseModals}
                            >
                                Cancelar
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                onClick={handleSaveCredenciais}
                                loading={isSubmitingCredenciais}
                            >
                                Salvar
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </>
    )
}
